import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash.omit';

import { getRadsFromCenter } from './helpers';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  localElement: PropTypes.shape({
    cardElement: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    cardElementConfiguration: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setLocalElement: PropTypes.func.isRequired,
  cardBounds: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  cardElementBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
};

const CardDesignerCardElementRotateControls = ({
  element,
  localElement,
  setLocalElement,
  cardBounds,
  cardElementBounds,
  onCardElementUpdate,
}) => {
  const [rotating, setRotating] = useState(null);
  const [rotateData, setRotateData] = useState(null);
  const [rotatedElement, setRotatedElement] = useState(false);
  const [initialMousePosition, setInitialMousePosition] = useState({});
  const { frame } = localElement.cardElement;

  useEffect(() => {
    const onRotate = event => {
      const { clientX, clientY } = event;
      const { x: cardX, y: cardY } = cardBounds;
      const { x, y, width, height } = cardElementBounds;
      const {
        x: initialClientX = clientX,
        y: initialClientY = clientY,
      } = initialMousePosition;

      const toppedCardX = Math.floor(cardX ?? 0);
      const toppedCardY = Math.floor(cardY ?? 0);

      const centerX = toppedCardX + x + width / 2;
      const centerY = toppedCardY + y + height / 2;

      const initialDegrees = getRadsFromCenter(
        { x: centerX, y: centerY },
        { x: initialClientX, y: initialClientY },
      );
      const currentDegrees = getRadsFromCenter(
        { x: centerX, y: centerY },
        { x: clientX, y: clientY },
      );

      const degreeChange = frame.rotation + currentDegrees - initialDegrees;

      setRotatedElement(true);
      setRotateData({
        rotation: degreeChange,
      });

      setLocalElement({
        ...localElement,
        cardElement: {
          ...localElement.cardElement,
          frame: {
            ...frame,
            rotation: degreeChange,
          },
        },
      });
    };

    const onStopRotate = () => {
      window.removeEventListener('mousemove', onRotate);
      setRotating(false);
    };

    if (rotating) {
      window.addEventListener('mousemove', onRotate);
      window.addEventListener('mouseup', onStopRotate);
    }

    return () => {
      window.removeEventListener('mousemove', onRotate);
      window.removeEventListener('mouseup', onStopRotate);
    };
  }, [rotating]);

  useEffect(() => {
    // We have finished rotating the element when the rotator resets.
    // If at said point, we have rotated the element, and have rotate data,
    // update the objects accordingly.
    if (!rotating && rotatedElement && rotateData) {
      onCardElementUpdate({
        cardElement: element.cardElement,
        data: {
          frame: {
            ...omit(frame, '__typename'),
            ...rotateData,
          },
        },
      });
      setRotatedElement(false);
      setRotateData(null);
    }
  }, [rotating, rotatedElement, rotateData]);

  const onRotatorMouseDown = event => {
    const { clientX, clientY } = event;
    setRotating(true);
    setInitialMousePosition({ x: clientX, y: clientY });
  };

  return (
    <Fragment>
      <div
        role="button"
        tabIndex={0}
        aria-label="Rotate"
        className="card-designer-card-element-rotator"
        onMouseDown={onRotatorMouseDown}
      />
    </Fragment>
  );
};

CardDesignerCardElementRotateControls.propTypes = propTypes;

export default CardDesignerCardElementRotateControls;
